import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
const inputFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getComparableTime = (date) => {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return getComparableTime(a) === getComparableTime(b);
};

const isWithinRange = (date, start, end) => {
  if (!start || !end) return false;
  const time = getComparableTime(date);
  const startTime = getComparableTime(start);
  const endTime = getComparableTime(end);

  return time > startTime && time < endTime;
};

const generateCalendar = (visibleMonth) => {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const totalCells = 42; // stable 6 weeks layout

  return Array.from({ length: totalCells }, (_, index) => {
    const dayNumber = index - firstDayIndex + 1;
    const cellDate = new Date(year, month, dayNumber);
    const inCurrentMonth = cellDate.getMonth() === month;

    return { date: cellDate, inCurrentMonth };
  });
};

const CustomRangePicker = ({ range, onRangeChange, onApply, onCancel }) => {
  const [visibleMonth, setVisibleMonth] = useState(() => range?.start || new Date());

  useEffect(() => {
    if (range?.start) {
      setVisibleMonth(range?.start);
    }
  }, [range?.start]);

  const calendarDays = useMemo(() => generateCalendar(visibleMonth), [visibleMonth]);

  const handlePrevMonth = () => {
    setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleDayClick = (day) => {
    const dateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());

    if (!range?.start || (range?.start && range?.end)) {
      onRangeChange({ start: dateOnly, end: null });
      return;
    }

    if (dateOnly < range?.start) {
      onRangeChange({ start: dateOnly, end: range?.start });
    } else if (isSameDay(dateOnly, range?.start)) {
      onRangeChange({ start: dateOnly, end: dateOnly });
    } else {
      onRangeChange({ start: range?.start, end: dateOnly });
    }
  };

  const handleApplyClick = () => {
    if (range?.start && range?.end) {
      onApply();
    }
  };

  return (
    <div className="w-full max-w-md rounded-3xl border border-[#E4E7EB] bg-white p-3 shadow-lg absolute right-0 z-10">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="rounded-full p-2 text-[#1E1E1E] hover:bg-[#F5F6F7]"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-base font-semibold text-[#1E1E1E]">
          {monthFormatter.format(visibleMonth)}
        </span>
        <button
          type="button"
          onClick={handleNextMonth}
          className="rounded-full p-2 text-[#1E1E1E] hover:bg-[#F5F6F7]"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="flex items-center justify-between text-xs font-medium text-[#8F94A1]">
        {daysOfWeek.map((day) => (
          <span key={day} className="w-10 text-center">
            {day}
          </span>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-7 gap-y-2 text-sm">
        {calendarDays.map(({ date, inCurrentMonth }) => {
          const isStart = isSameDay(date, range?.start);
          const isEnd = isSameDay(date, range?.end);
          const inRange = isWithinRange(date, range?.start, range?.end);

          const cellClasses = [
            'relative mx-auto flex h-10 w-[39px] text-[12px] items-center justify-center rounded-full transition',
            !inCurrentMonth ? 'text-[#C7CBD7]' : 'text-[#1E1E1E]',
            isStart || isEnd ? 'bg-[#1E1E1E] text-white' : 'hover:bg-[#F5F6F7]',
            !isStart && !isEnd && inRange ? 'bg-[#F0F5FF]' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => handleDayClick(date)}
              className={cellClasses}
            >
              <span>{date.getDate()}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-1 flex flex-col gap-4 rounded-2xl bg-[#F7F8FA] p-2 md:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-2xl border border-[#E4E7EB] bg-white px-3 py-2">
          <CalendarIcon size={18} className="text-[#5C5F6E]" />
          <div className="flex flex-col text-xs">
            <span className="text-[#8F94A1]">Check-in</span>
            <span className="text-xs font-semibold text-[#1E1E1E]">
              {range?.start ? inputFormatter.format(range?.start) : '--'}
            </span>
          </div>
        </div>
        <div className="flex flex-1 items-center gap-2 rounded-2xl border border-[#E4E7EB] bg-white px-3 py-2">
          <CalendarIcon size={18} className="text-[#5C5F6E]" />
          <div className="flex flex-col text-xs">
            <span className="text-[#8F94A1]">Check-out</span>
            <span className="text-xs font-semibold text-[#1E1E1E]">
              {range?.end ? inputFormatter.format(range?.end) : '--'}
            </span>
          </div>
        </div>
        {/* <button
          type="button"
          onClick={handleApplyClick}
          disabled={!(range?.start && range?.end)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white transition enabled:hover:bg-primary disabled:bg-[#2039648a]"
        >
          <ArrowRight size={20} />
        </button> */}
      </div>

      <div className="mt-4 flex justify-end gap-3 text-sm font-medium">
        <button type="button" className="text-[#8F94A1] hover:text-[#1E1E1E] transition duration-300 cursor-pointer" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="text-primary hover:text-btntext disabled:text-[#A5D1E2] cursor-pointer transition duration-300"
          onClick={handleApplyClick}
          disabled={!(range?.start && range?.end)}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default CustomRangePicker;